import { Row } from '@riac/design-system';
import dayjs from 'dayjs';
import React from 'react';
import styled from 'styled-components';

const defaults = {
  startDateFormat: 'MMMM Do',
  endDateFormat: 'MMMM Do, YYYY',
  timeFormat: 'h:mm a',
};

// We want at least these fields, but we will accept(and ignore) others
export type AuctionData = {
  AuctionID: number | string,
  StartDate: string,
  EndDate: string,
  StartTime: string,
  [field: string]: any,
};

export type AuctionDateProps = React.PropsWithChildren<{
  auction: AuctionData,
  startDateFormat?: string,
  dateSpacer?: string,
  endDateFormat?: string,
  timeSpacer?: string,
  timeFormat?: string,
  omitTime?: boolean,
  gap?: string,
  italic?: boolean,
  bold?: boolean,
  class?: string,
  style?: Record<string, any>,
  justifyContent?: string,
}>;

/* NOTE: We need to keep this component simple.  Avoid the urge to kitchen sink it to
* accomodate any possible combination of dates/times.
*
* The original problem this component aims to solve is that during the redesign
* we incurred a regression where a page that used to display the full date range
* for multi day auctions was only showing the start date.
* This component handles the logic to determine display of single date or date range
* and it will display it in a standardized layout so we can use it and avoid future regressions.
*
* [StartDate][DateSep][EndDate][TimeSep][Time]
*
* If you can't get the display you are going for through the props or styled components
* Then your use case probably isn't appropriate for this component.
*/
export const AuctionDate = (props: AuctionDateProps): JSX.Element => {
  const start = dayjs(props.auction.StartDate + 'T' + props.auction.StartTime);
  const end = dayjs(props.auction.EndDate + 'T' + '11:59:59');
  const isMultiDay = end.diff(start, 'day') > 0;

  const dateSpacer = props.dateSpacer || '-';

  const startDateFormat = props.startDateFormat || defaults.startDateFormat;
  const endDateFormat = props.endDateFormat || defaults.endDateFormat;
  const timeFormat = props.timeFormat || defaults.timeFormat;

  return (
    <AuctionDateContainer
      gap = '0.25em'
      {...props}
    >
      {isMultiDay
        ? (
          <>
            <AuctionDateStartDate>{start.format(startDateFormat)}</AuctionDateStartDate>
            <AuctionDateDateSpacer>{dateSpacer}</AuctionDateDateSpacer>
            <AuctionDateEndDate>
              {end.format(endDateFormat)}
            </AuctionDateEndDate>
          </>
        )
        : (
          <AuctionDateStartDate>{start.format(endDateFormat)}</AuctionDateStartDate>
        )
      }
      {(!props.omitTime && props.timeSpacer) && (
        <AuctionDateTimeSpacer>{props.timeSpacer}</AuctionDateTimeSpacer>
      )}
      {!props.omitTime && (
        <AuctionDateTime>{start.format(timeFormat)}</AuctionDateTime>
      )}
    </AuctionDateContainer>
  );
};

export const AuctionDateContainer = styled(Row)<AuctionDateProps>`
  ${({ italic }) => italic ? 'font-style: italic;' : ''}
  ${({ bold }) => bold ? 'font-weight: bold;' : ''}
    justify-content: ${({ justifyContent }) => justifyContent ? justifyContent : 'initial'};
`;

export const AuctionDateStartDate = styled.div``;
export const AuctionDateStartYear = styled.div``;
export const AuctionDateDateSpacer = styled.div``;
export const AuctionDateEndDate = styled.div``;
export const AuctionDateEndYear = styled.div``;
export const AuctionDateTimeSpacer = styled.div``;
export const AuctionDateTime = styled.div``;
